import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Stack, Typography } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import {
  ConceptGroupMetaStrip,
  ProjectDataGrid,
  type MetaStripItem,
} from '@verantos-dev/feature-concept-group-detail';
import {
  downloadPreviewCsv,
  type ConceptGroupPreviewRuleItem,
} from '@verantos-dev/feature-epi-chat';

import { DefinitionActionPill, renderCheck } from '../components/DefinitionActionPill';

// --- Rules / Definition tab (DataGrid: a row per seed) ---------------------

export const DraftRulesTab = ({
  loading,
  error,
  ruleItems,
  previewId,
  conceptGroupName,
}: {
  loading: boolean;
  error: boolean;
  ruleItems: ConceptGroupPreviewRuleItem[];
  previewId: string | null;
  conceptGroupName: string;
}) => {
  const { formatMessage } = useIntl();

  const columns = useMemo<GridColDef<ConceptGroupPreviewRuleItem>[]>(
    () => [
      {
        field: 'conceptId',
        headerName: formatMessage({ id: 'epiChat.conceptGroupPreview.definition.col.omopId' }),
        // The cell renders "<conceptId> · <vocabularyId>" (e.g. "443615 · SNOMED").
        minWidth: 150,
        flex: 1,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Typography variant="body2">
            {params.row.conceptId}
            {params.row.vocabularyId ? ` · ${params.row.vocabularyId}` : ''}
          </Typography>
        ),
      },
      {
        field: 'concept',
        headerName: formatMessage({ id: 'epiChat.conceptGroupPreview.definition.col.conceptName' }),
        flex: 2,
        minWidth: 200,
        // The one sortable column: client-side sort on the concept name (via the
        // valueGetter); the header toggles asc <-> desc.
        sortable: true,
        filterable: false,
        valueGetter: (_value, row) => row.conceptName ?? String(row.conceptId),
      },
      {
        field: 'action',
        headerName: formatMessage({ id: 'epiChat.conceptGroupPreview.definition.col.action' }),
        width: 130,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <DefinitionActionPill isExcluded={Boolean(params.row.isExcluded)} />
        ),
      },
      {
        field: 'descendants',
        headerName: formatMessage({ id: 'epiChat.conceptGroupPreview.rules.col.descendants' }),
        width: 130,
        sortable: false,
        filterable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => renderCheck(Boolean(params.row.includeDescendants)),
      },
      {
        field: 'mapped',
        headerName: formatMessage({ id: 'epiChat.conceptGroupPreview.rules.col.mapped' }),
        width: 110,
        sortable: false,
        filterable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => renderCheck(Boolean(params.row.includeMapped)),
      },
    ],
    [formatMessage]
  );

  if (error) {
    return (
      <Typography variant="body2" color="error" data-testid="cg-preview-rules-error">
        {formatMessage({ id: 'epiChat.conceptGroupPreview.rules.loadError' })}
      </Typography>
    );
  }

  // Name + Domain(s) both live in the page header now — the strip only carries
  // the concept count (and the download).
  const metaItems: MetaStripItem[] = [
    {
      key: 'concepts',
      icon: <CheckCircleIcon fontSize="small" color="success" />,
      label: formatMessage({ id: 'epiChat.conceptGroupPreview.meta.conceptsLabel' }),
      value: ruleItems.length,
    },
  ];

  return (
    <Stack sx={{ gap: 2.5, height: '100%', minHeight: 0 }} data-testid="cg-preview-rules-tab">
      {/* OR-6288: the "Summary" rationale block is dropped from the Definition
          tab per the design (the agent's explanation lives in the RA chat). */}
      <Stack sx={{ gap: 1.5, flex: 1, minHeight: 0 }}>
        <ConceptGroupMetaStrip
          items={metaItems}
          onDownload={previewId ? () => downloadPreviewCsv(previewId, conceptGroupName) : undefined}
          testIdPrefix="cg-preview-definition"
        />
        {/* Fill the remaining tab height and let the grid scroll its rows
            internally (mirroring the Concepts tab). An earlier version used
            ``autoHeight`` + page-scroll, but ``autoHeight`` disables the grid's
            internal scroller, so a long rule list (>~8 rows) was clipped with no
            way to reach the rest. A bounded, flex-filled grid scrolls natively. */}
        <ProjectDataGrid<ConceptGroupPreviewRuleItem>
          id="cg-preview-rules"
          rows={ruleItems}
          columns={columns}
          getRowId={(row) => String(row.conceptId)}
          loading={loading}
          density="compact"
          disableColumnMenu
          sortingOrder={['asc', 'desc']}
          hideFooterPagination
          noRowsLabel={formatMessage({ id: 'epiChat.conceptGroupPreview.rules.empty' })}
        />
      </Stack>
    </Stack>
  );
};
